import { CopyIcon, DeleteIcon, PlusSquareIcon } from "@chakra-ui/icons";
import {
  Button,
  Checkbox,
  Flex,
  FlexProps,
  Grid,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  InputProps,
  InputRightAddon,
  Skeleton,
  Text,
  useToast,
} from "@chakra-ui/react";
import { Post } from "api";
import useAdmin from "hooks/useAdmin";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PartnerData } from "views/admin/partnerDetail/types";

interface PartnerDetailProps {
  data: PartnerData;
  partnerId: number;
}

type PartnerDataInput = {
  title: string;
  key: keyof Omit<
    PartnerData,
    | "apps"
    | "partnerEmail"
    | "redeemedCoupons"
    | "deductions"
    | "accountManagers"
    | "shouldGenerateInvoice"
  >;
  inputProps?: InputProps;
  containerProps?: FlexProps;
  canBeCopied?: boolean;
  leftAddon?: React.ReactNode;
  rightAddon?: React.ReactNode;
};

const PartnerDetailView = (props: PartnerDetailProps) => {
  const { partnerId, data } = props;
  const toast = useToast();
  const isAdmin = useAdmin();

  const [partnerData, setPartnerData] = useState<PartnerData>(data);
  const [partnerDataChanged, setPartnerDataChanged] = useState<boolean>(false);
  const [secondaryEmail, setSecondaryEmail] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const PARTNER_COMPANY_DATA_INPUTS: PartnerDataInput[] = useMemo(() => {
    if (isAdmin)
      return [
        {
          title: "Name",
          key: "partnerName",
        },
        {
          title: "Country",
          key: "companyCountry",
        },
        {
          title: "City",
          key: "companyCity",
        },
        {
          title: "Postal Code",
          key: "companyPostalCode",
        },
        {
          title: "Street",
          key: "companyStreet",
          containerProps: {
            gridColumn: "span 2",
          },
        },
        {
          title: "VAT ID",
          key: "companyVatId",
          containerProps: {
            gridColumn: "span 2",
          },
        },
        {
          title: "Invoice Email",
          key: "primaryInvoiceMail",
          containerProps: {
            gridColumn: "span 4",
          },
          canBeCopied: true,
        },
      ];

    return [
      {
        title: "Invoice Email",
        key: "primaryInvoiceMail",
        containerProps: {
          gridColumn: "span 4",
        },
        canBeCopied: true,
      },
    ];
  }, [isAdmin]);

  useEffect(() => {
    setPartnerData(data);
  }, [data]);

  const isEmptyValues = useMemo(() => {
    if (!partnerData) return true;

    for (let key in partnerData) {
      const excludedKeys = [
        "apps",
        "apiKey",
        "audienceApiKey",
        "secondaryInvoiceMails",
        "companyVatId",
        "accountManagers",
        "deductions",
        "redeemedCoupons",
        "shouldGenerateInvoice",
      ];

      if (excludedKeys.includes(key)) continue;

      if (partnerData[key as keyof PartnerData] === "") return true;
    }
  }, [partnerData]);

  const handleCompanyDataFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, item: PartnerDataInput) => {
      if (!partnerDataChanged) setPartnerDataChanged(true);

      setPartnerData((prevValues) => ({
        ...prevValues,
        [item.key]: e.target.value,
      }));
    },
    [partnerDataChanged]
  );

  const validateEmail = useCallback((email: string) => {
    if (!email) return false;
    if (!email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) return false;
    return true;
  }, []);

  const handleAddSecondaryEmail = useCallback(() => {
    if (!secondaryEmail) return;
    if (!validateEmail(secondaryEmail)) {
      toast({
        title: "Invalid Email",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    setPartnerData((prevData) => ({
      ...prevData,
      secondaryInvoiceMails: [
        ...prevData.secondaryInvoiceMails,
        secondaryEmail,
      ],
    }));
    setSecondaryEmail("");
    setPartnerDataChanged(true);
    toast({
      title: "Secondary finance email added. Make sure save the details.",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  }, [secondaryEmail, validateEmail, toast]);

  const updatePartnerCompanyDetails = useCallback(
    async (_partnerId: number, _data: PartnerData) => {
      if (!_partnerId || !_data) return;
      setLoading(true);

      let data = {
        ..._data,
        companyVatId: _data.companyVatId === "" ? null : _data.companyVatId,
        companyName: _data.partnerName,
        shouldGenerateInvoice: _data.shouldGenerateInvoice,
        invoicePrimaryMail: _data.primaryInvoiceMail,
        invoiceSecondaryMail:
          _data.secondaryInvoiceMails.length > 0
            ? _data.secondaryInvoiceMails.join(",")
            : null,
      };
      delete data.apps;
      delete data.partnerName;
      delete data.apiKey;
      delete data.audienceApiKey;
      delete data.redeemedCoupons;
      delete data.secondaryInvoiceMails;
      delete data.primaryInvoiceMail;
      delete data.deductions;
      delete data.accountManagers;

      try {
        const response = await Post(
          `/manage/partners/${_partnerId}/update`,
          data
        );

        if (response?.err) {
          toast({
            status: "error",
            title: "Failed to Update Partner",
            description:
              response?.err ||
              "An error occurred while updating partner details.",
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        toast({
          status: "success",
          title: "Success",
          description: "Partner details updated successfully.",
          duration: 3000,
          isClosable: true,
        });

        setPartnerDataChanged(false);
      } catch (error: any) {
        console.warn(error);
        toast({
          status: "error",
          title: "Failed to Update Partner",
          description:
            error?.err ||
            "An unexpected error occurred while updating partner details.",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  return (
    <Flex flexDir={"column"} bgColor="white" borderRadius={"16"} p={4} mt={8}>
      {partnerData ? (
        <Heading color={"#5028F4"} fontSize={"xl"} fontWeight="normal" mb={1}>
          <b>{partnerData.partnerName}</b> ({partnerData.partnerEmail})
        </Heading>
      ) : (
        <Skeleton h={8} mb={1} borderRadius={"8"} />
      )}

      {partnerData ? (
        <>
          <Grid templateColumns="repeat(4, 1fr)" gap={4} mt={4} mb={4}>
            <Flex gridColumn={"span 4"}>
              <Checkbox
                defaultChecked={partnerData?.shouldGenerateInvoice}
                onChange={(e) =>
                  setPartnerData((prevData) => ({
                    ...prevData,
                    shouldGenerateInvoice: e.target.checked,
                  }))
                }
              >
                Should Generate Invoice
              </Checkbox>
            </Flex>
            {PARTNER_COMPANY_DATA_INPUTS.map((item, index: number) => (
              <Flex
                key={`partner detail input - ${index}`}
                flexDir={"column"}
                {...item.containerProps}
              >
                <Text mb={2} fontWeight={"semibold"} color="gray.800">
                  {item.title}
                </Text>
                <InputGroup>
                  {item.leftAddon ? (
                    <InputLeftAddon
                      _hover={{ cursor: "pointer" }}
                      children={item.leftAddon}
                      onClick={() =>
                        toast({
                          title: "API Key copied to clipboard.",
                          status: "success",
                          duration: 2000,
                          isClosable: true,
                        })
                      }
                    />
                  ) : null}
                  <Input
                    value={partnerData[item.key]}
                    name={item.key as string}
                    onChange={(e) => handleCompanyDataFieldChange(e, item)}
                    placeholder={item.title}
                    autoComplete="somerandomstring"
                    {...item.inputProps}
                  />
                  {item.rightAddon || item.canBeCopied ? (
                    <InputRightAddon
                      _hover={{ cursor: "pointer" }}
                      children={
                        item.canBeCopied ? (
                          <IconButton
                            aria-label="Copy API Key"
                            icon={<CopyIcon />}
                          />
                        ) : (
                          item.rightAddon
                        )
                      }
                      onClick={
                        item.canBeCopied && typeof item.key === "string"
                          ? () => {
                              //@ts-ignore
                              handleCopyValue(partnerData[item.key]);
                              toast({
                                title: "Copied to clipboard.",
                                status: "success",
                                duration: 2000,
                                isClosable: true,
                              });
                            }
                          : () => {}
                      }
                    />
                  ) : null}
                </InputGroup>
              </Flex>
            ))}
            <Text
              mb={2}
              fontWeight={"semibold"}
              color="gray.800"
              gridColumn={"span 4"}
            >
              Invoice CC
            </Text>
            {partnerData.secondaryInvoiceMails.length > 0 ? (
              <Flex gridColumn={"span 4"} flexDir="column">
                {partnerData.secondaryInvoiceMails.map((email, index) => (
                  <InputGroup mb={4}>
                    <Input
                      value={email}
                      autoComplete="somerandomstring"
                      name={`secondary-email-${index}`}
                      onChange={(e) => {
                        if (!partnerDataChanged) setPartnerDataChanged(true);
                        setPartnerData((prevData) => ({
                          ...prevData,
                          secondaryInvoiceMails:
                            prevData.secondaryInvoiceMails.map((item, i) =>
                              i === index ? e.target.value : item
                            ),
                        }));
                      }}
                      placeholder={"email@example.com"}
                    />

                    <InputRightAddon
                      _hover={{ cursor: "pointer" }}
                      children={
                        <IconButton
                          color={"red.500"}
                          aria-label="Delete Secondary Finance Email"
                          icon={<DeleteIcon />}
                        />
                      }
                      onClick={() => {
                        if (!partnerDataChanged) setPartnerDataChanged(true);
                        setPartnerData((prevData) => ({
                          ...prevData,
                          secondaryInvoiceMails:
                            prevData.secondaryInvoiceMails.filter(
                              (_, i) => i !== index
                            ),
                        }));
                        toast({
                          title: "Secondary finance email removed.",
                          status: "success",
                          duration: 2000,
                          isClosable: true,
                        });
                      }}
                    />
                    <InputRightAddon
                      _hover={{ cursor: "pointer" }}
                      children={
                        <IconButton
                          aria-label="Copy Secondary Finance Email"
                          icon={<CopyIcon />}
                        />
                      }
                      onClick={() => {
                        //@ts-ignore
                        handleCopyValue(email);
                        toast({
                          title: "Copied to clipboard.",
                          status: "success",
                          duration: 2000,
                          isClosable: true,
                        });
                      }}
                    />
                  </InputGroup>
                ))}
              </Flex>
            ) : (
              <></>
            )}
            <Flex
              key={`partner detail input - secondary emails`}
              flexDir={"column"}
              gridColumn={"span 2"}
            >
              <InputGroup mb={4}>
                <Input
                  autoComplete="somerandomstring"
                  value={secondaryEmail}
                  name={"new-secondary-email"}
                  borderColor="purple.200"
                  bgColor={"purple.50"}
                  onChange={(e) => setSecondaryEmail(e.target.value)}
                  placeholder={"Add new Invoice CC Email"}
                  enterKeyHint={"send"}
                  type={"email"}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAddSecondaryEmail();
                    }
                  }}
                />

                <InputRightAddon
                  _hover={{ cursor: "pointer" }}
                  bgColor={"purple.100"}
                  children={
                    <IconButton
                      color={"purple.500"}
                      bgColor={"purple.100"}
                      aria-label="Add New Secondary Email"
                      _hover={{ cursor: "pointer", bgColor: "purple.200" }}
                      icon={<PlusSquareIcon />}
                    />
                  }
                  onClick={() => {
                    handleAddSecondaryEmail();
                  }}
                />
              </InputGroup>
            </Flex>
          </Grid>
          <Button
            bgColor={"brand.500"}
            color="white"
            _hover={{ backgroundColor: "brand.600" }}
            isDisabled={loading || !partnerData || !!isEmptyValues}
            alignSelf={"flex-end"}
            isLoading={loading}
            onClick={() => updatePartnerCompanyDetails(partnerId, partnerData)}
          >
            Save Details
          </Button>
        </>
      ) : (
        <>
          <Skeleton h={8} mb={2} borderRadius={"8"} />
          <Skeleton h={8} borderRadius={"8"} />
        </>
      )}
    </Flex>
  );
};

export default PartnerDetailView;
